/**
 * Labstep
 *
 * @module components/Comment/Body
 * @desc Comment body
 */

import { CommentFormEditContainer as CommentFormEdit } from 'labstep-web/components/Comment/Form/Edit';
import { FileViewer } from 'labstep-web/components/File/Viewer';
import { MoleculeItem } from 'labstep-web/components/Molecule/Item';
import { RichTextItem } from 'labstep-web/components/RichText/Item';
import { SequencePreview } from 'labstep-web/components/Sequence/Preview';
import { TableItemContainer as TableItem } from 'labstep-web/components/Table/Item';
import UserShowProfile from 'labstep-web/components/User/Show/Profile';
import { ICONS } from 'labstep-web/constants/icons';
import { Icon } from 'labstep-web/core/Icon';
import { Link } from 'labstep-web/core/Link';
import { printBody } from 'labstep-web/core/Mentions/utils';
import { Pre } from 'labstep-web/core/Pre';
import type React from 'react';
import styles from './styles.module.scss';
import type {
  APIResponseBodyProps,
  ICommentBodyProps,
  IMessageProps,
} from './types';

export const Message: React.FC<IMessageProps> = ({ comment }) =>
  comment.body && comment.body.length > 0 ? (
    <Pre>
      {printBody(comment.body, (part) => (
        <UserShowProfile
          key={part.id}
          viewComponent={`@${part.name}`}
          user={
            comment.user_mentions.find(
              (userMention) =>
                userMention.user.id === Number(part.id),
            )!.user
          }
        />
      ))}
    </Pre>
  ) : (
    <div />
  );

export const Body: React.FC<APIResponseBodyProps> = ({
  comment,
  toggled,
  toggle,
}) => (
  <div className={styles.commentBody} data-testid="comment-body">
    {toggled ? (
      <CommentFormEdit comment={comment} toggle={toggle} />
    ) : (
      <Message comment={comment} />
    )}
  </div>
);

export const CommentBody: React.FC<ICommentBodyProps> = ({
  comment,
  toggled,
  toggle,
  fileViewerProps,
  dataTableProps,
  richTextProps,
}) => (
  <>
    {comment.resource && (
      <div className={styles.resourceContainer}>
        <span>
          <Icon name={ICONS.resource.primary} />
          <Link
            to="resource_show"
            params={{ id: comment.resource.id }}
          >
            {comment.resource.name}
          </Link>
        </span>
      </div>
    )}
    {comment.files && comment.files.length > 0 && (
      <FileViewer file={comment.files[0]} {...fileViewerProps} />
    )}
    {!(comment.data_table || comment.rich_text) && (
      <Body toggled={toggled} comment={comment} toggle={toggle} />
    )}
    {comment.data_table && (
      <TableItem
        table={comment.data_table}
        {...dataTableProps}
        allowLocking
      />
    )}
    {comment.rich_text && (
      <RichTextItem richText={comment.rich_text} {...richTextProps} />
    )}
    {comment.molecule && <MoleculeItem parent={comment} />}
    {comment.sequence && (
      <SequencePreview sequence={comment.sequence} />
    )}
  </>
);
